import {
  approveMemberPhoto,
  getMemberPhoto,
  rejectMemberPhoto,
  uploadMemberPhoto,
  type MemberPhotoContract,
} from '@urbanik/api-client';
import { CanonicalModal, Icon, optimizeImageFile } from '@urbanik/ui';
import { useEffect, useMemo, useState } from 'react';
import { apiBase } from './api-base';
export function MemberPhotoEditor({
  memberId,
  name,
  canRead,
  canManage,
}: {
  memberId: string;
  name: string;
  canRead: boolean;
  canManage: boolean;
}) {
  const [official, setOfficial] = useState<MemberPhotoContract | null>(null),
    [pending, setPending] = useState<MemberPhotoContract | null>(null),
    [open, setOpen] = useState(false),
    [file, setFile] = useState<File | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [reason, setReason] = useState('Actualización administrativa de fotografía.');
  const localPreview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(
    () => () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    },
    [localPreview],
  );
  useEffect(() => {
    if (!canRead) return;
    const c = new AbortController();
    getMemberPhoto(apiBase, memberId, c.signal)
      .then((r) => setOfficial(r.photo))
      .catch(() => undefined);
    return () => c.abort();
  }, [canRead, memberId]);
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((x) => x[0])
    .join('')
    .toUpperCase();
  const src = official
    ? `${apiBase}${official.thumbnailUrl}?v=${encodeURIComponent(official.decidedAt ?? official.createdAt)}`
    : null;
  const pendingSrc = pending
    ? `${apiBase}${pending.thumbnailUrl}?v=${encodeURIComponent(pending.createdAt)}`
    : localPreview;
  const close = () => {
    if (busy) return;
    setOpen(false);
    setFile(null);
    setPending(null);
    setError('');
  };
  const upload = async () => {
    if (!file) {
      setError('Selecciona o toma una fotografía.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const r = await uploadMemberPhoto(apiBase, memberId, file);
      setPending(r.photo);
      setFile(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos cargar la fotografía.');
    } finally {
      setBusy(false);
    }
  };
  const decide = async (target: 'approve' | 'reject') => {
    if (!pending) return;
    if (reason.trim().length < 10) {
      setError('Escribe un motivo de al menos 10 caracteres.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const r =
        target === 'approve'
          ? await approveMemberPhoto(apiBase, memberId, pending.id, { reason })
          : await rejectMemberPhoto(apiBase, memberId, pending.id, { reason });
      if (target === 'approve') setOfficial(r.photo);
      setOpen(false);
      setFile(null);
      setPending(null);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos decidir la fotografía.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="member-photo-block">
        <div
          className="member-photo-avatar"
          aria-label={official ? `Fotografía de ${name}` : `Avatar de ${name}`}
        >
          {src ? <img src={src} alt={`Fotografía de ${name}`} /> : <span>{initials}</span>}
        </div>
        {canManage ? (
          <button
            type="button"
            className="ui-row-action member-photo-action"
            onClick={() => setOpen(true)}
            title="Gestionar fotografía"
            aria-label="Gestionar fotografía"
          >
            <Icon name="camera" />
          </button>
        ) : null}
      </div>
      <CanonicalModal
        open={open}
        size="medium"
        eyebrow="Fotografía del socio"
        title="Gestionar fotografía"
        description="La carga queda pendiente hasta que confirmes su aprobación."
        onClose={close}
      >
        <section className="member-photo-editor">
          <div className="member-photo-preview">
            {pendingSrc ? (
              <img src={pendingSrc} alt="Vista previa de la fotografía" />
            ) : (
              <div>
                <Icon name="camera" />
                <span>Sin fotografía seleccionada</span>
              </div>
            )}
          </div>
          {!pending ? (
            <>
              <label className="ui-field member-photo-file">
                <span>Fotografía</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="environment"
                  onChange={(e) => {
                    const candidate = e.target.files?.[0] ?? null;
                    if (!candidate) setFile(null);
                    else if (typeof createImageBitmap !== 'function') setFile(candidate);
                    else void optimizeImageFile(candidate).then(setFile);
                    e.target.value = '';
                  }}
                />
                <small className="ui-field__hint">
                  JPEG, PNG o WebP; máximo 5 MiB; mínimo 256 × 256.
                </small>
              </label>
              <div className="member-status-actions">
                <button type="button" className="ui-button ui-button--secondary" onClick={close}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="ui-button ui-button--primary"
                  onClick={() => void upload()}
                  disabled={busy}
                >
                  {busy ? 'Procesando…' : 'Cargar para revisión'}
                </button>
              </div>
            </>
          ) : (
            <>
              <label className="ui-field member-photo-reason">
                <span>Motivo administrativo</span>
                <textarea
                  value={reason}
                  maxLength={500}
                  onChange={(e) => setReason(e.target.value)}
                />
                <small className="ui-field__hint">
                  La fotografía oficial no cambiará hasta aprobar.
                </small>
              </label>
              <div className="member-status-actions">
                <button
                  type="button"
                  className="ui-button ui-button--secondary"
                  onClick={() => void decide('reject')}
                  disabled={busy}
                >
                  Rechazar
                </button>
                <button
                  type="button"
                  className="ui-button ui-button--primary"
                  onClick={() => void decide('approve')}
                  disabled={busy}
                >
                  {busy ? 'Guardando…' : 'Aprobar fotografía'}
                </button>
              </div>
            </>
          )}
          {error ? (
            <p className="member-status-error" role="alert">
              {error}
            </p>
          ) : null}
        </section>
      </CanonicalModal>
    </>
  );
}
